import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import type {
  IRowNode,
  IServerSideSelectionState,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import useDebounce from '@/components/hooks/useDebounce';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { PrimaryContext } from '@/services/context';
import {
  type MatingsSourceType,
  onFarmImplantRecordIdsOptions,
  OnFarmImplantRequestError,
  toRecordKey,
} from '@/services/on-farm-implant';
import { useToast } from '@/services/toast';
import { useContextMenu } from '@/utils/context-menu';

import { useOnFarmImplantDataSource } from '../../hooks/useOnFarmImplantDataSource';
import type { LoadedSource, MatingRecord } from '../../types';
import { getLoadedSourceColumnDefs } from '../../utils/columnDefs';

const PAGE_SIZE = 20;

const GRID_SELECT_ALL_SOURCES = new Set<string>([
  'uiSelectAll',
  'uiSelectAllCurrentPage',
  'uiSelectAllFiltered',
]);

const SOURCE_TYPE_MAP: Record<string, MatingsSourceType> = {
  fresh: 'fresh',
  frozen: 'frozen',
  flush: 'flush',
  'client-on-farm-tog': 'client-on-farm',
};

const getNodeRecordKey = (node: IRowNode): string | null => {
  const record = node.data as MatingRecord | undefined;
  return record?.id ? toRecordKey(record.id, record.accountId) : null;
};

type LoadedSourceCardProps = {
  source: LoadedSource;
  index: number;
  siteId: string;
  accountId?: string;
  selectedRecordKeys: string[];
  onSelectedRecordKeysChange: (sourceId: string, recordKeys: string[]) => void;
  readOnly?: boolean;
  onRemove: (sourceId: string) => void;
};

export const LoadedSourceCard = ({
  source,
  index,
  siteId,
  accountId,
  selectedRecordKeys,
  onSelectedRecordKeysChange,
  readOnly = false,
  onRemove,
}: LoadedSourceCardProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const gridRef = useRef<AgGridReact>(null);
  const isApplyingSelectionRef = useRef(false);

  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const debouncedSearch = useDebounce(searchText, 300);

  const apiSourceType = SOURCE_TYPE_MAP[source.sourceId];

  const selectedRecordKeysRef = useRef(selectedRecordKeys);

  const filters = useMemo(
    () => ({
      siteId,
      accountId,
      searchTerm: debouncedSearch || undefined,
    }),
    [siteId, accountId, debouncedSearch],
  );

  const applySelectionToGrid = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    const selected = new Set(selectedRecordKeysRef.current);
    const toSelect: IRowNode[] = [];
    const toDeselect: IRowNode[] = [];

    api.forEachNode((node) => {
      const recordKey = getNodeRecordKey(node);
      if (!recordKey) return;
      const shouldSelect = selected.has(recordKey);
      if (node.isSelected() === shouldSelect) return;
      (shouldSelect ? toSelect : toDeselect).push(node);
    });

    if (toSelect.length === 0 && toDeselect.length === 0) return;

    isApplyingSelectionRef.current = true;
    try {
      if (toDeselect.length > 0) {
        api.setNodesSelected({
          nodes: toDeselect,
          newValue: false,
          source: 'api',
        });
      }
      if (toSelect.length > 0) {
        api.setNodesSelected({
          nodes: toSelect,
          newValue: true,
          source: 'api',
        });
      }
    } finally {
      isApplyingSelectionRef.current = false;
    }
  }, []);

  const { dataSource } = useOnFarmImplantDataSource(
    apiSourceType,
    filters,
    setIsLoading,
    applySelectionToGrid,
  );

  useEffect(() => {
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [filters]);

  useEffect(() => {
    selectedRecordKeysRef.current = selectedRecordKeys;
    applySelectionToGrid();
  }, [applySelectionToGrid, selectedRecordKeys]);

  const handleSelectAllChange = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        onSelectedRecordKeysChange(source.sourceId, []);
        return;
      }
      if (!apiSourceType || !siteId) return;

      setIsSelectingAll(true);
      try {
        const response = await queryClient.fetchQuery(
          onFarmImplantRecordIdsOptions({
            sourceType: apiSourceType,
            siteId,
            accountId,
            search: debouncedSearch || undefined,
          }),
        );
        onSelectedRecordKeysChange(
          source.sourceId,
          response.records.map((record) =>
            toRecordKey(record.recordId, record.accountId),
          ),
        );
      } catch (error) {
        gridRef.current?.api?.deselectAll('api');
        applySelectionToGrid();
        const detail =
          error instanceof OnFarmImplantRequestError && error.problems.length
            ? error.problems.join(' ')
            : undefined;
        toast.show({
          severity: 'error',
          summary: t('onFarmImplant.sourceCard.selectAllFailed'),
          detail,
        });
      } finally {
        setIsSelectingAll(false);
      }
    },
    [
      accountId,
      apiSourceType,
      applySelectionToGrid,
      debouncedSearch,
      onSelectedRecordKeysChange,
      queryClient,
      siteId,
      source.sourceId,
      t,
      toast,
    ],
  );

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent) => {
      if (isApplyingSelectionRef.current) return;
      if (event.source === 'api') return;

      if (GRID_SELECT_ALL_SOURCES.has(event.source)) {
        const state =
          event.api.getServerSideSelectionState() as IServerSideSelectionState | null;
        void handleSelectAllChange(state?.selectAll ?? false);
        return;
      }

      const loadedKeys = new Set<string>();
      const nextSelected = new Set<string>();
      event.api.forEachNode((node) => {
        const recordKey = getNodeRecordKey(node);
        if (!recordKey) return;
        loadedKeys.add(recordKey);
        if (node.isSelected()) {
          nextSelected.add(recordKey);
        }
      });

      const retained = selectedRecordKeysRef.current.filter(
        (key) => !loadedKeys.has(key),
      );
      onSelectedRecordKeysChange(source.sourceId, [
        ...retained,
        ...nextSelected,
      ]);
    },
    [handleSelectAllChange, onSelectedRecordKeysChange, source.sourceId],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    [],
  );

  const columnDefs = useMemo(() => {
    const defs = getLoadedSourceColumnDefs(t);
    if (!readOnly) return defs;
    return defs.map((def) =>
      def.field === 'checkbox'
        ? { ...def, checkboxSelection: false, headerCheckboxSelection: false }
        : def,
    );
  }, [readOnly, t]);

  const selectedCount = selectedRecordKeys.length;
  const allSelected =
    source.totalRecords > 0 && selectedCount >= source.totalRecords;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <i
            data-drag-handle
            className="pi pi-bars cursor-grab text-sm text-gray-400"
          />
          <div className="flex size-6 items-center justify-center rounded bg-gray-100 text-xs font-bold text-gray-600">
            {index + 1}
          </div>
          <span className="text-sm">{source.icon}</span>
          <span className="font-medium text-gray-900">{source.title}</span>
          <span className="text-sm text-gray-500">
            {source.totalRecords} {t('onFarmImplant.sourceCard.records')}
          </span>
          <span className="text-sm text-gray-500">
            {source.totalEmbryos} {t('onFarmImplant.sourceCard.embryos')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isSelectingAll && (
            <i className="pi pi-spin pi-spinner text-xs text-gray-400" />
          )}
          <Checkbox
            aria-label={t('onFarmImplant.sourceCard.selectAll')}
            checked={allSelected}
            indeterminate={someSelected}
            disabled={readOnly || isSelectingAll}
            onCheckedChange={(checked) => {
              void handleSelectAllChange(checked);
            }}
          />
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.sourceCard.selectAll')}
          </span>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.sourceCard.selectedCount', {
              selected: selectedCount,
              total: source.totalRecords,
            })}
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            disabled={readOnly}
            onClick={() => onRemove(source.sourceId)}
          >
            <i className="pi pi-times" />
          </Button>
        </div>
      </div>

      <div className="px-4 py-2">
        <div className="mb-2 flex items-center justify-between">
          <div className="relative w-48">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t(
                'onFarmImplant.sourceCard.filterRecordsPlaceholder',
              )}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={handleSearchChange}
            />
            {isLoading && (
              <i className="pi pi-spin pi-spinner absolute top-1/2 right-2 -translate-y-1/2 text-xs text-gray-400" />
            )}
          </div>
          <span className="text-xs text-gray-500">
            {source.totalRecords} {t('onFarmImplant.sourceCard.records')}
          </span>
        </div>

        <div
          className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[200px]`}
        >
          <WithLocalStorage storageKey={`on-farm-implant-source-${source.id}`}>
            <AgGridReact
              ref={gridRef}
              gridOptions={gridOptions}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              headerHeight={36}
              rowHeight={48}
              rowSelection="multiple"
              suppressRowClickSelection
              getRowId={getRowId<MatingRecord>((params) =>
                toRecordKey(params.data.id, params.data.accountId),
              )}
              getContextMenuItems={getContextMenuItems}
              rowModelType="serverSide"
              serverSideDatasource={dataSource}
              cacheBlockSize={PAGE_SIZE}
              blockLoadDebounceMillis={300}
              onSelectionChanged={handleSelectionChanged}
              onModelUpdated={applySelectionToGrid}
              onStoreRefreshed={applySelectionToGrid}
            />
          </WithLocalStorage>
        </div>
      </div>
    </div>
  );
};
